import { useState } from "react";
import { Mail, Crown, AlertCircle } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useInviteMate } from "@/hooks/useMates";
import { useToastHandler } from "@/hooks/useToastHandler";
import { useUserInfo } from "@/hooks/useUserInfo";

interface InviteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function InviteDialog({ open, onOpenChange }: InviteDialogProps) {
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const inviteMutation = useInviteMate();
  const { handleSuccess } = useToastHandler();
  const { data: user } = useUserInfo();
  const navigate = useNavigate();
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Reset error
    setEmailError("");

    // Validate email
    if (!email.trim()) {
      setEmailError("Email is required");
      return;
    }

    const normalizedEmail = email.toLowerCase().trim();
    if (!validateEmail(normalizedEmail)) {
      setEmailError("Please enter a valid email address");
      return;
    }

    try {
      await inviteMutation.mutateAsync({ email: normalizedEmail });
      // Show success message and close dialog
      handleSuccess(`Invitation sent to ${normalizedEmail}.`, 3000);
      // Query invalidation handled by the mutation
      setEmail("");
      setEmailError("");
      onOpenChange(false);
    } catch {
      // Error is handled by the mutation's onError handler
    }
  };

  const handleClose = () => {
    if (!inviteMutation.isPending) {
      setEmail("");
      setEmailError("");
      onOpenChange(false);
    }
  };

  const isSubscribed = user?.subscription?.is_subscribed || false;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="p-6 border-0 overflow-hidden max-w-md bg-white">
        <DialogTitle className="text-2xl font-bold">
          Invite by Email
        </DialogTitle>
        <div className="border-t border-gray-200"></div>

        {!isSubscribed ? (
          // Show subscription required message for free users
          <>
            <DialogDescription className="text-tx-default text-base">
              Inviting new mates is a{" "}
              <Link
                to="/settings/subscription"
                className="underline underline-offset-4"
              >
                Pro feature
              </Link>
              , but you can still join groups by accepting invites from your
              friends.
            </DialogDescription>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-amber-800">
                    Pro Plan Required
                  </p>
                  <p className="text-sm text-amber-700 mt-1">
                    Upgrade to Pro to invite workout mates and access all
                    features.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex justify-between space-x-3">
              <Button
                variant="outline"
                size="lg"
                type="button"
                onClick={handleClose}
                className="border-gray-300"
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="lg"
                type="button"
                className="flex-1"
                onClick={() => {
                  handleClose();
                  // Navigate to subscription page - you might want to use navigate here
                  navigate("/settings/subscription", { replace: true });
                }}
              >
                <Crown className="h-5 w-5 mr-2" />
                Upgrade
              </Button>
            </div>
          </>
        ) : (
          // Show invite form for subscribed users
          <>
            <DialogDescription className="text-tx-default text-base">
              Send an invitation to your friend's email address.
            </DialogDescription>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Input
                  type="email"
                  placeholder="Friend's email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={inviteMutation.isPending}
                  autoFocus
                  className={emailError ? "border-red-500" : ""}
                />
                {emailError && (
                  <p className="text-sm text-tx-error">{emailError}</p>
                )}
              </div>

              <div className="flex justify-between space-x-3">
                <Button
                  variant="outline"
                  size="lg"
                  type="button"
                  onClick={handleClose}
                  disabled={inviteMutation.isPending}
                  className="border-gray-300"
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  size="lg"
                  type="submit"
                  className="flex-1"
                  disabled={inviteMutation.isPending || !email.trim()}
                >
                  <Mail className="h-5 w-5 mr-2" />
                  {inviteMutation.isPending ? "Sending..." : "Send Invite"}
                </Button>
              </div>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
